import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { match } from 'ts-pattern'

import Header from '@/components-latest/layout/Header'
import Banner from '@/components-latest/primitives/Banner'
import { InfoCircle } from '@/components-latest/primitives/Icons'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import CampaignsTable from '@/components-latest/ui/CampaignsPage/CampaignsTable'
import EmptyCampaignsPage from '@/components-latest/ui/CampaignsPage/EmptyCampaignsPage'
import { useCampaignsFilters } from '@/components-latest/ui/CampaignsPage/useCampaignsFilters'
import { isErrorType } from '@/config/http/http.helpers'
import type { AppDispatch } from '@/config/store/store'
import {
  campaignsApi,
  useFetchCampaignsQuery
} from '@/ducks/campaigns/api/campaigns.api'
import type { CampaignsCacheSnapshot } from '@/ducks/campaigns/campaigns.cache.helpers'
import {
  applyCustomDateFilterToCampaignsResponse,
  isValidCustomCampaignDateRange
} from '@/ducks/campaigns/campaigns.filters.helpers'
import {
  type CampaignLimit,
  type CampaignsResponse,
  DEFAULT_CAMPAIGN_LIMIT
} from '@/ducks/campaigns/campaigns.types'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'

import Styles from './MarketingCampaignsPage.module.scss'

type CampaignsLocationState = {
  resetCampaignsPagination?: boolean
  campaignsBaselineTotal?: number
  campaignsBaselineIds?: string[]
}

const POST_CREATE_POLL_INTERVAL_MS = 2500
const POST_CREATE_MAX_POLLS = 15

const hasCampaignListUpdated = (
  baseline: CampaignsCacheSnapshot,
  data: CampaignsResponse
): boolean => {
  if (data.total > baseline.total) return true

  const currentIds = data.campaigns.map(campaign => campaign.campaignId)
  if (currentIds.length > baseline.campaignIds.length) return true

  return currentIds.some(id => !baseline.campaignIds.includes(id))
}

const MarketingCampaignsPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [page, setPage] = useState(0)
  const [limit, setLimit] = useState<CampaignLimit>(DEFAULT_CAMPAIGN_LIMIT)
  const [isPostCreateSync, setIsPostCreateSync] = useState(false)
  const postCreateBaselineRef = useRef<CampaignsCacheSnapshot | null>(null)
  const postCreatePollCountRef = useRef(0)

  const handlePageReset = useCallback(() => {
    setPage(0)
    setIsPostCreateSync(false)
    postCreateBaselineRef.current = null
  }, [])

  const {
    filters,
    queryParams,
    hasActiveFilters,
    handlers
  } = useCampaignsFilters({
    page,
    limit,
    onPageReset: handlePageReset
  })

  useEffect(() => {
    const navState = location.state as CampaignsLocationState | null
    if (!navState?.resetCampaignsPagination) return

    setPage(0)
    setLimit(DEFAULT_CAMPAIGN_LIMIT)
    postCreatePollCountRef.current = 0
    postCreateBaselineRef.current = {
      total: navState.campaignsBaselineTotal ?? 0,
      campaignIds: navState.campaignsBaselineIds ?? []
    }
    setIsPostCreateSync(true)
    history.replace(location.pathname)
  }, [history, location.pathname, location.state])

  const { data, isFetching, isLoading, isError, error } = useFetchCampaignsQuery(
    queryParams,
    { refetchOnMountOrArgChange: true }
  )

  useEffect(() => {
    if (!isError || !error) return

    dispatch(
      addSystemNotification({
        message:
          (isErrorType(error) && error.message) ||
          t('marketing.campaigns.fetch-error'),
        intent: 'error'
      })
    )
  }, [dispatch, error, isError, t])

  useEffect(() => {
    const baseline = postCreateBaselineRef.current
    if (!baseline || !data) return

    if (hasCampaignListUpdated(baseline, data)) {
      setIsPostCreateSync(false)
      postCreateBaselineRef.current = null
    }
  }, [data])

  useEffect(() => {
    if (
      !isPostCreateSync ||
      page !== 0 ||
      limit !== DEFAULT_CAMPAIGN_LIMIT ||
      hasActiveFilters
    ) {
      return
    }

    const pollParams = { page: 0, limit: DEFAULT_CAMPAIGN_LIMIT }
    let cancelled = false

    const pollForFreshCampaigns = async () => {
      if (cancelled) return

      await (dispatch as AppDispatch)(
        campaignsApi.endpoints.fetchCampaigns.initiate(pollParams, {
          forceRefetch: true
        })
      )
        .unwrap()
        .catch(() => {})

      postCreatePollCountRef.current += 1

      if (postCreatePollCountRef.current >= POST_CREATE_MAX_POLLS) {
        setIsPostCreateSync(false)
        postCreateBaselineRef.current = null
      }
    }

    pollForFreshCampaigns()
    const intervalId = window.setInterval(
      pollForFreshCampaigns,
      POST_CREATE_POLL_INTERVAL_MS
    )

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [dispatch, hasActiveFilters, isPostCreateSync, limit, page])

  const hasCustomDateFilter = isValidCustomCampaignDateRange(
    filters.dateFrom,
    filters.dateTo
  )

  const displayData = useMemo(() => {
    if (!data) return undefined
    if (hasCustomDateFilter && isFetching) return undefined
    if (!hasCustomDateFilter) return data

    return applyCustomDateFilterToCampaignsResponse(
      data,
      filters.dateFrom!,
      filters.dateTo!
    )
  }, [data, filters.dateFrom, filters.dateTo, hasCustomDateFilter, isFetching])

  const showPageSpinner =
    (isLoading && !data) || (hasCustomDateFilter && isFetching)

  const handleLimitChange = (nextLimit: CampaignLimit) => {
    setLimit(nextLimit)
    setPage(0)
    setIsPostCreateSync(false)
    postCreateBaselineRef.current = null
  }

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage)
    setIsPostCreateSync(false)
    postCreateBaselineRef.current = null
  }

  const showCampaignsTable =
    !showPageSpinner &&
    !isError &&
    (hasActiveFilters || page > 0 || (displayData?.total ?? 0) > 0)

  const showUnfilteredEmpty =
    page === 0 &&
    !hasActiveFilters &&
    !isFetching &&
    !showPageSpinner &&
    !isError &&
    (displayData?.total ?? 0) === 0

  return (
    <div className={Styles.page}>
      <Header title={t('marketing.campaigns.title')} />

      <div className={Styles.content}>
        <Banner
          className={Styles.yassirInfoBanner}
          description={t('marketing.campaigns.yassir-managed-banner')}
          icon={<InfoCircle />}
          type="boxed"
          state="normal"
        />

        <Text
          variant="heading-04"
          weight="semiBold"
          className={Styles.sectionTitle}>
          {t('marketing.campaigns.offers-section')}
        </Text>

        {match({
          showPageSpinner,
          showUnfilteredEmpty,
          showCampaignsTable
        })
          .when(
            ({ showPageSpinner }) => showPageSpinner,
            () => (
              <div className={Styles.spinner}>
                <Spinner />
              </div>
            )
          )
          .when(
            ({ showUnfilteredEmpty }) => showUnfilteredEmpty,
            () => <EmptyCampaignsPage />
          )
          .when(
            ({ showCampaignsTable }) => showCampaignsTable,
            () => (
              <CampaignsTable
                campaigns={displayData?.campaigns ?? []}
                page={page}
                limit={limit}
                total={displayData?.total ?? 0}
                filters={filters}
                hasActiveFilters={hasActiveFilters}
                campaignsQueryParams={queryParams}
                handlers={handlers}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            )
          )
          .otherwise(() => (
            <EmptyCampaignsPage />
          ))}
      </div>
    </div>
  )
}

export default MarketingCampaignsPage
